import "@/i18n/config";
import React, { useEffect, useState } from "react";

import {
  createFeedback,
  getFeedbacks,
} from "@/client_app/components/CommunityMemberDetails/services/FetchAPIService";
import { Tagger } from "@/client_app/components/CommunityMemberDetails/TabSection";
import { Comment } from "@/common/components/Comment";
import { PrimaryButton, SecondaryButton } from "@/ui/buttons";

interface Props {
  positionId: string;
  inAdmin?: boolean;
  taggers?: Tagger[];
}

interface FeedbackData {
  id: string;
  date: string;
  description: string;
  status: string | null;
  updated_at: string;
  isEditing: boolean;
  document?: File | string | null;
  document_url: string;
}

export function Feedback(props: Props) {
  const { positionId, inAdmin, taggers } = props;
  const [feedbackData, setFeedbackData] = useState<FeedbackData[]>([]);
  useEffect(() => {
    if (positionId) {
      getFeedbacks(positionId, inAdmin)
        .then((response) => response.json())
        .then((data) => {
          setFeedbackData(data.feedbacks);
        });
    }
  }, [positionId]);

  const [backupData, setBackupData] = useState<FeedbackData[]>([]);

  const handleInputChange = (index: number, key: string, value: any) => {
    const updatedData = [...feedbackData];
    updatedData[index] = { ...updatedData[index], [key]: value };
    setFeedbackData(updatedData);
  };

  const toggleEditMode = (index: number, enable: boolean) => {
    const updatedData = [...feedbackData];
    updatedData[index] = {
      ...updatedData[index],
      isEditing: enable,
      updated_at: new Date().toString(),
    };

    if (enable) {
      // Save a backup of the row data when entering edit mode
      setBackupData((prev) => ({
        ...prev,
        [index]: { ...feedbackData[index] },
      }));
    } else {
      // Clear the backup when exiting edit mode
      setBackupData((prev) => {
        const updatedBackup = { ...prev };
        delete updatedBackup[index];
        return updatedBackup;
      });
    }

    setFeedbackData(updatedData);
  };

  const options = [
    ["pending", "Pending"],
    ["in-progress", "In-progress"],
    ["completed", "Completed"],
  ];
  const cancelEdit = (index: number) => {
    const isNewRow = !feedbackData[index]?.id;
    if (isNewRow) {
      // Remove the new row entirely if it's canceled
      const updatedData = feedbackData.filter((_, i) => i !== index);
      setFeedbackData(updatedData);
    } else {
      // Revert to the original data for existing rows
      const updatedData = [...feedbackData];
      updatedData[index] = { ...backupData[index], isEditing: false }; // Restore original data and exit edit mode
      setFeedbackData(updatedData);
      setBackupData((prev) => {
        const updatedBackup = { ...prev };
        delete updatedBackup[index];
        return updatedBackup;
      });
    }
  };

  const addRow = () => {
    setFeedbackData((prev) => [
      {
        date: "",
        id: "",
        description: "",
        status: "pending",
        updated_at: "",
        document_url: "",
        isEditing: true,
      },
      ...prev,
    ]);
  };

  const saveRow = (index: number) => {
    const rowData = feedbackData[index];
    const formData = new FormData();

    formData.append("feedback[id]", rowData.id);
    formData.append("feedback[date]", rowData.date);
    formData.append("feedback[description]", rowData.description);
    formData.append("feedback[status]", rowData.status || "");
    if (rowData.document) {
      formData.append("feedback[document]", rowData.document);
    }

    createFeedback(positionId, formData, inAdmin)
      .then((response) => response.json())
      .then((data) => {
        setFeedbackData((prevData) => {
          const updatedData = [...prevData];
          updatedData[index] = {
            ...updatedData[index],
            ...data,
            isEditing: false,
          };
          return updatedData;
        });
      });
  };
  return (
    <div>
      {!inAdmin && (
        <div className='flex justify-end max-w'>
          <PrimaryButton label='Add New' onClick={addRow} />
        </div>
      )}
      <table
        className='w-full text-left border-separate table-auto'
        style={{ borderSpacing: "0 24px" }}
      >
        <colgroup>
          <col style={{ width: "25%" }} />
          <col style={{ width: "15%" }} />
          <col style={{ width: "20%" }} />
          <col style={{ width: "10%" }} />
          <col style={{ width: "10%" }} />
          <col style={{ width: "10%" }} />
          <col style={{ width: "auto" }} />
        </colgroup>
        <thead>
          <tr>
            <th>Description</th>
            <th>Addressed Date</th>
            <th>Document</th>
            <th>Status</th>
            <th>Updated At</th>
            <th>Comments</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {feedbackData.map(
            (
              {
                date,
                id,
                description,
                status,
                updated_at,
                isEditing,
                document_url,
              },
              index
            ) => (
              <>
                <tr key={index}>
                  <td>
                    {isEditing ? (
                      <input
                        type='text'
                        value={description}
                        onChange={(e) =>
                          handleInputChange(
                            index,
                            "description",
                            e.target.value
                          )
                        }
                        className='form-control'
                      />
                    ) : (
                      description
                    )}
                  </td>
                  <td>
                    {isEditing ? (
                      <div>
                        <input
                          type='date'
                          value={date}
                          onChange={(e) =>
                            handleInputChange(index, "date", e.target.value)
                          }
                          className='form-control'
                        />
                      </div>
                    ) : (
                      new Date(date).toLocaleDateString("en-GB")
                    )}
                  </td>
                  <td>
                    {isEditing ? (
                      <input
                        type='file'
                        accept='.pdf,.doc,.docx,.jpg,.png'
                        onChange={(e) =>
                          handleInputChange(
                            index,
                            "document",
                            e.target.files?.[0] || null
                          )
                        }
                      />
                    ) : (
                      document_url && (
                        <a
                          href={document_url}
                          target='_blank'
                          className='underline'
                        >
                          Attached
                        </a>
                      )
                    )}
                  </td>
                  <td className='capitalize'>
                    {isEditing ? (
                      <select
                        defaultValue={status}
                        className='form-control'
                        onChange={(e) =>
                          handleInputChange(index, "status", e.target.value)
                        }
                      >
                        {options.map(([key, value]) => (
                          <option key={key} value={key}>
                            {value}
                          </option>
                        ))}
                      </select>
                    ) : (
                      status
                    )}
                  </td>
                  <td>
                    {updated_at && new Date(updated_at).toLocaleString("en-GB")}
                  </td>
                  <td>
                    <Comment
                      recordId={id}
                      title={`Feedback: ${new Date(date).toLocaleDateString(
                        "en-GB"
                      )}`}
                      recordType='Feedback'
                      inAdmin={inAdmin}
                      taggers={taggers}
                    />
                  </td>
                  <td>
                    {isEditing ? (
                      <div className='flex gap-2'>
                        <PrimaryButton
                          label='Save'
                          size='SMALL'
                          onClick={() => saveRow(index)}
                        />
                        <SecondaryButton
                          label='Cancel'
                          size='SMALL'
                          onClick={() => cancelEdit(index)}
                        />
                      </div>
                    ) : (
                      <SecondaryButton
                        label='Edit'
                        size='SMALL'
                        onClick={() => toggleEditMode(index, true)}
                      />
                    )}
                  </td>
                </tr>
              </>
            )
          )}
        </tbody>
      </table>
    </div>
  );
}
